import { normalizeError } from 'core/common/errors';
import { SpanAttributes, SpanName } from 'core/common/observability/entities';
import { ATTRS_ANALYTICS_EVENT_NAME } from 'core/common/observability/entities/SemanticAttributes';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import { ErrorMonitoring } from 'core/common/services/errorMonitoring';
import { AxonEventNames } from '../../entities';
import { AxonService } from '../../interfaces';
import { AxonServiceDecorator } from './AxonServiceDecorator';

export class AxonServiceWithMonitoring extends AxonServiceDecorator {
  constructor(
    axonService: AxonService,
    private readonly observabilitySystem: ObservabilitySystem,
    private readonly errorMonitoringService: ErrorMonitoring,
    private readonly receiveAdditionalObservabilityAttributes?: () => SpanAttributes,
  ) {
    super(axonService);
  }

  track(eventName: AxonEventNames, data?: Record<string, unknown>): void {
    if (!super.isAxonRequired()) {
      try {
        return super.track(eventName, data);
      } catch {
        this.errorMonitoringService.captureError('Axon track failed', {
          tags: {
            error_message: 'Axon track failed',
            code: 'axon_track_failed_when_not_required',
          },
        });
        return;
      }
    }

    if (super.isAxonRequired() && !super.isAxonAvailable()) {
      try {
        return super.track(eventName, data);
      } catch {
        this.errorMonitoringService.captureError('Axon track failed', {
          tags: {
            error_message: 'Axon track failed',
            code: 'axon_track_failed_when_required_not_available',
          },
        });
        return;
      }
    }

    const span = this.observabilitySystem.startSpan(SpanName.TRACK_ANALYTICS_AXON);
    const spanAttributes = {
      [ATTRS_ANALYTICS_EVENT_NAME]: eventName,
      ...(this.receiveAdditionalObservabilityAttributes
        ? this.receiveAdditionalObservabilityAttributes()
        : {}),
    } as SpanAttributes;
    span.setAttributes(spanAttributes);

    try {
      const result = super.track(eventName, data);
      span.succeed();
      return result;
    } catch (err) {
      const error = normalizeError(err);
      span.fail(error);
      return;
    }
  }
}
